import React, { useEffect, useRef, useState } from 'react';
import { splitAddress } from '../../utils/addressFormat';

/**
 * Gibt eine Adresse so aus, dass sie – wenn der Platz nicht reicht – genau
 * zwischen Strasse und «PLZ Ort» umbricht:
 *
 *   einzeilig:  St. Gallerstrasse 50, 8400 Winterthur
 *   zweizeilig: St. Gallerstrasse 50
 *               8400 Winterthur
 *
 * Beide Teile tragen `white-space: nowrap`, das Leerzeichen dazwischen ist
 * damit die einzige Umbruchstelle – es kann nie mitten im Strassennamen oder
 * zwischen PLZ und Ort gebrochen werden. Nebeneffekt, auf den sich die
 * Startadresse im Banner stützt: die min-content-Breite des Elements sinkt
 * auf den längeren der beiden Teile, statt die volle Adresse zu verlangen.
 * Dadurch kann ein Flex-Nachbar (dort der Filter-Button) auf dem Bildschirm
 * bleiben, sobald der Umbruch möglich ist.
 *
 * Das Komma entfällt in der zweizeiligen Variante. Ob umgebrochen wurde,
 * lässt sich nur messen (Vergleich der offsetTop-Werte), beobachtet per
 * ResizeObserver.
 *
 * Ausgeblendet wird mit `visibility: hidden`, nicht mit `display: none`:
 * das Komma behält seinen Platz, die Umbruchentscheidung bleibt also
 * unabhängig davon, ob es sichtbar ist. Andernfalls könnte eine grenzwertig
 * lange Adresse endlos flackern – Komma weg, passt; Komma da, passt nicht.
 */
export const AddressLines = ({ address, street, plz, city, className = '' }) => {
  const { streetLine, cityLine } = splitAddress(address, street, plz, city);
  const containerRef = useRef(null);
  const streetRef = useRef(null);
  const cityRef = useRef(null);
  const [wrapped, setWrapped] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !cityLine) return undefined;

    // Der ResizeObserver feuert direkt beim observe() einmal von selbst,
    // deshalb braucht es keine zusätzliche Messung beim Mount.
    const observer = new ResizeObserver(() => {
      const streetEl = streetRef.current;
      const cityEl = cityRef.current;
      if (streetEl && cityEl) {
        setWrapped(cityEl.offsetTop > streetEl.offsetTop);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cityLine]);

  if (!cityLine) {
    return <span className={className}>{streetLine}</span>;
  }

  return (
    <span ref={containerRef} className={className}>
      <span ref={streetRef} className="address-part">
        {streetLine}
        <span className={`address-comma${wrapped ? ' address-comma--hidden' : ''}`}>,</span>
      </span>
      {' '}
      <span ref={cityRef} className="address-part">{cityLine}</span>
    </span>
  );
};

export default AddressLines;
